import { useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, FileUp, Loader2, Sparkles, UploadCloud } from "lucide-react";

import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Field, SkillChip } from "@/components/common/Primitives";
import { emptyCandidate } from "./CandidateForm";
import { FormGrid, SelectField, TagsField, TextField } from "./Fields";

export default function ResumeUpload({ open, onOpenChange, onSaved }) {
  const fileRef = useRef(null);
  const [step, setStep] = useState("upload");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState(emptyCandidate);
  const [mergeInto, setMergeInto] = useState(null);

  const reset = () => {
    setStep("upload");
    setResult(null);
    setForm(emptyCandidate);
    setMergeInto(null);
  };

  const upload = async (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["pdf", "doc", "docx"].includes(ext)) return toast.error("Upload a PDF, DOC or DOCX file");
    setBusy(true);
    setStep("parsing");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/candidates/parse-resume", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const p = data.parsed || {};
      setResult(data);
      setForm({
        ...emptyCandidate,
        ...Object.fromEntries(Object.entries(p).filter(([, v]) => v !== null && v !== undefined)),
        skills: p.skills || [],
        technologies: p.technologies || [],
        certifications: p.certifications || [],
        education: p.education || [],
        companies: p.companies || [],
        projects: p.projects || [],
        industry: p.industry || "Automotive",
        source: "Sourced",
      });
      setStep("review");
      toast.success("Resume parsed — review the extracted details");
    } catch (e) {
      toast.error(apiError(e, "Resume parsing failed"));
      setStep("upload");
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      ["id", "candidate_code"].forEach((k) => delete payload[k]);
      const { data } = await api.post("/candidates/from-resume", {
        candidate: payload,
        resume_path: result?.resume_path,
        resume_filename: result?.resume_filename,
        resume_text: result?.resume_text,
        merge_into: mergeInto,
      });
      toast.success(mergeInto ? "Existing candidate updated from resume" : "Candidate created from resume");
      onOpenChange(false);
      reset();
      onSaved?.(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) reset();
      }}
    >
      <DialogContent className="max-h-[92vh] max-w-3xl border-slate-200 bg-white p-0">
        <DialogHeader className="border-b border-slate-200 px-6 py-5">
          <DialogTitle className="flex items-center gap-2 text-lg font-medium text-slate-900">
            <Sparkles className="h-4 w-4 text-violet-600" /> Resume upload &amp; parsing
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            We extract structured details from the resume. Nothing is saved until you approve it.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(92vh-180px)]">
          <div className="px-6 py-6">
            {step === "upload" && (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                data-testid="resume-dropzone"
                className="flex w-full flex-col items-center gap-3 rounded-md border border-dashed border-slate-200 bg-slate-50 px-6 py-14 transition-colors hover:border-blue-300"
              >
                <UploadCloud className="h-7 w-7 text-slate-500" />
                <span className="text-sm text-slate-600">Click to select a resume</span>
                <span className="text-xs text-slate-400">PDF, DOC or DOCX · up to 10MB</span>
              </button>
            )}

            {step === "parsing" && (
              <div className="flex flex-col items-center gap-3 py-16">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                <p className="text-sm text-slate-600">Extracting profile details…</p>
                <p className="text-xs text-slate-400">This usually takes a few seconds.</p>
              </div>
            )}

            {step === "review" && (
              <div className="space-y-5">
                {result?.duplicates?.length > 0 && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-4" data-testid="resume-duplicate-warning">
                    <p className="flex items-center gap-2 text-sm font-medium text-amber-800">
                      <AlertTriangle className="h-4 w-4" /> Possible duplicate candidates
                    </p>
                    <div className="mt-3 space-y-2">
                      {result.duplicates.map((d) => (
                        <label key={d.id} className="flex cursor-pointer items-start gap-2.5 text-xs text-amber-900/80">
                          <input
                            type="radio"
                            name="merge"
                            checked={mergeInto === d.id}
                            onChange={() => setMergeInto(d.id)}
                            className="mt-0.5"
                            data-testid="merge-radio"
                          />
                          <span>
                            <strong className="text-amber-800">{d.full_name}</strong> · {d.email || "no email"} ·{" "}
                            {d.reasons.join(", ")} — update this record instead
                          </span>
                        </label>
                      ))}
                      <label className="flex cursor-pointer items-center gap-2.5 text-xs text-amber-900/80">
                        <input type="radio" name="merge" checked={!mergeInto} onChange={() => setMergeInto(null)} />
                        Create a brand new candidate record
                      </label>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Extracted from {result?.resume_filename}. Fields left blank were not found in the resume.
                </div>

                <FormGrid>
                  <TextField label="Full name" value={form.full_name} onChange={set("full_name")} testid="parsed-name-input" required />
                  <TextField label="Email" value={form.email} onChange={set("email")} testid="parsed-email-input" />
                  <TextField label="Phone" value={form.phone} onChange={set("phone")} />
                  <TextField label="Location" value={form.location} onChange={set("location")} />
                  <TextField label="Current company" value={form.current_company} onChange={set("current_company")} />
                  <TextField label="Current designation" value={form.current_designation} onChange={set("current_designation")} />
                  <TextField label="Total experience (yrs)" type="number" value={form.total_experience_years} onChange={set("total_experience_years")} />
                  <SelectField label="Industry" value={form.industry} onChange={set("industry")} options={["Automotive", "Semiconductor", "Software"]} />
                  <TextField label="Current CTC" value={form.current_ctc} onChange={set("current_ctc")} />
                  <TextField label="Expected CTC" value={form.expected_ctc} onChange={set("expected_ctc")} />
                  <TextField label="Notice period" value={form.notice_period} onChange={set("notice_period")} />
                  <TextField label="LinkedIn" value={form.linkedin} onChange={set("linkedin")} />
                </FormGrid>

                <TagsField label="Skills extracted" values={form.skills} onChange={set("skills")} testid="parsed-skills-input" />
                <TagsField label="Certifications" values={form.certifications} onChange={set("certifications")} />

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="surface rounded-md p-4">
                    <p className="label-xs mb-3">Experience detected</p>
                    {form.companies?.length ? (
                      <ul className="space-y-2.5">
                        {form.companies.map((c, i) => (
                          <li key={i} className="text-xs text-slate-600">
                            <span className="text-slate-900">{c.designation || "Role not available"}</span> ·{" "}
                            {c.company}
                            <span className="block text-slate-400">
                              {c.start || "?"} — {c.end || "?"}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs text-slate-400">Not available</p>
                    )}
                  </div>
                  <div className="surface rounded-md p-4">
                    <p className="label-xs mb-3">Education detected</p>
                    {form.education?.length ? (
                      <ul className="space-y-2.5">
                        {form.education.map((e, i) => (
                          <li key={i} className="text-xs text-slate-600">
                            {e.degree}
                            <span className="block text-slate-400">
                              {e.institution || "Institution not available"} · {e.year || "year not available"}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs text-slate-400">Not available</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.doc,.docx"
          className="hidden"
          data-testid="resume-file-input"
          onChange={(e) => upload(e.target.files?.[0])}
        />

        <DialogFooter className="border-t border-slate-200 px-6 py-4">
          {step === "review" ? (
            <>
              <Button variant="ghost" onClick={reset} className="text-slate-500">
                <FileUp className="mr-2 h-3.5 w-3.5" /> Upload a different file
              </Button>
              <Button onClick={save} disabled={busy} data-testid="resume-save-button" className="gap-2 rounded-full bg-blue-600 px-5 hover:bg-blue-500">
                {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {mergeInto ? "Update existing candidate" : "Approve & save candidate"}
              </Button>
            </>
          ) : (
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-500">
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
